import React, { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import axios from 'axios';
import { 
  Box, 
  Typography, 
  Grid, 
  TextField, 
  Button, 
  FormControlLabel, 
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Divider,
  RadioGroup,
  Radio,
  FormGroup
} from '@mui/material';
import { API_URL } from '../../config/api';

const validationSchema = yup.object({
  // Basic Information
  name: yup.string().required('Name is required'),
  employeeId: yup.string(),
  department: yup.string().required('Department is required'),
  designation: yup.string().required('Designation is required'),
  workLocation: yup.string().required('Work location is required'),
  
  // Resignation Details
  reasonForResignation: yup.string().required('Please select a reason'),
  otherReason: yup.string().when('reasonForResignation', {
    is: 'Other',
    then: () => yup.string().required('Please explain other reason'),
    otherwise: () => yup.string()
  }),
  thinkingDuration: yup.string().required('Please select thinking duration'),
  incidentsLedToDecision: yup.string().required('Please indicate if incidents led to decision'),
  scenarioExplanation: yup.string().when('incidentsLedToDecision', {
    is: 'true',
    then: () => yup.string().required('Please explain the scenario'),
    otherwise: () => yup.string()
  }),
  discussionPreference: yup.string().required('Please select discussion preference'),
  dateOfResignation: yup.date().required('Date of resignation is required'),
  proposedLastWorkingDay: yup.date().required('Proposed last working day is required'),
  reportingManager: yup.string().required('Reporting manager is required'),
  unitHead: yup.string().required('Unit head is required'),
  
  // Feedback
  feedback: yup.object({
    careerDevelopment: yup.string().required('Please answer career development question'),
    workloadManageable: yup.string().required('Please answer workload question'),
    skillsUtilized: yup.string().required('Please answer skills utilization question'),
    skillsUtilizedRating: yup.string().required('Please rate skills utilization'),
    supportFromManagerTeam: yup.string().required('Please answer support question'),
    supportExplanation: yup.string().when('supportFromManagerTeam', {
      is: 'false',
      then: () => yup.string().required('Please explain lack of support'),
      otherwise: () => yup.string()
    })
  }),
  
  // Final
  finalComments: yup.string(),
  acknowledgment: yup.boolean().oneOf([true], 'You must acknowledge the statement').required('You must acknowledge the statement')
});

const ResignationStep = ({ onFormSubmit, readOnly = false, userIdOverride }) => {
  const { user, getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(false);
  const [isUpdate, setIsUpdate] = useState(false);
  const effectiveUserId = userIdOverride || user?.id;
  const [userOptions, setUserOptions] = useState([]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      // Basic Information
      name: user ? `${user.firstName} ${user.lastName}` : '',
      employeeId:   '' ,
      department: user?.department || '',
      designation: user?.jobTitle || '',
      workLocation: '',
      
      // Resignation Details
      reasonForResignation: '',
      otherReason: '',
      thinkingDuration: '',
      incidentsLedToDecision: '',//false
      scenarioExplanation: '',
      discussionPreference: '',
      dateOfResignation: '',
      proposedLastWorkingDay: '',
      reportingManager: '',
      unitHead: '',
      reportingManagerEmail: '',
      unitHeadEmail: '',
      
      // Feedback
      feedback: {
        careerDevelopment: '',
        workloadManageable: '',
        skillsUtilized: '',
        skillsUtilizedRating: '',
        supportFromManagerTeam: '',
        supportExplanation: ''
      },
      
      // Final
      finalComments: '',
      acknowledgment: false
    },
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      try {
        let response;
        
        if (isUpdate) {
          // Update existing resignation
          const url = `${API_URL}/offboarding/resignation/user/${effectiveUserId}`;
          response = await axios.put(url, values, {
            headers: {
              ...getAuthHeaders(),
              'Content-Type': 'application/json'
            }
          });
          toast.success('Resignation form updated successfully!');
        } else {
          // Create new resignation
          const url = `${API_URL}/offboarding/resignation`;
          response = await axios.post(url, values, {
            headers: {
              ...getAuthHeaders(),
              'Content-Type': 'application/json'
            }
          });
          toast.success('Resignation form submitted successfully!');
        }
        
        // After successful submission, fetch the updated data to set update mode
        try {
          const fetchResponse = await axios.get(`${API_URL}/offboarding/resignation/user/${effectiveUserId}`, {
            headers: getAuthHeaders()
          });
          
          if (fetchResponse.data) {
            const formattedData = {
              ...fetchResponse.data,
              dateOfResignation: fetchResponse.data.dateOfResignation ? new Date(fetchResponse.data.dateOfResignation).toISOString().split('T')[0] : '',
              proposedLastWorkingDay: fetchResponse.data.proposedLastWorkingDay ? new Date(fetchResponse.data.proposedLastWorkingDay).toISOString().split('T')[0] : ''
            };
            formik.setValues(formattedData);
            setIsUpdate(true);
            console.log('Form updated to update mode after submission');
            
            // Trigger validation update in parent component
            if (onFormSubmit) {
              onFormSubmit();
            }
          }
        } catch (fetchError) {
          console.error('Error fetching updated data:', fetchError);
        }
        
      } catch (error) {
        const errorMessage = error.response?.data?.error || `Failed to ${isUpdate ? 'update' : 'submit'} resignation form`;
        toast.error(errorMessage);
        console.error(`Error ${isUpdate ? 'updating' : 'submitting'} resignation form:`, error);
      } finally {
        setLoading(false);
      }
    }
  });

  // Load existing resignation data if available
  useEffect(() => {
    if (effectiveUserId) {
      console.log('Fetching resignation data for user:', effectiveUserId);
      axios.get(`${API_URL}/offboarding/resignation/user/${effectiveUserId}`, {
        headers: getAuthHeaders()
      })
        .then(response => {
          console.log('GET API Response:', response.data);
          const data = response.data;
          if (data) {
            const formattedData = {
              ...data,
              dateOfResignation: data.dateOfResignation ? new Date(data.dateOfResignation).toISOString().split('T')[0] : '',
              proposedLastWorkingDay: data.proposedLastWorkingDay ? new Date(data.proposedLastWorkingDay).toISOString().split('T')[0] : ''
            };
            console.log('Formatted data:', formattedData);
            formik.setValues(formattedData);
            setIsUpdate(true);
          }
        })
        .catch(error => {
          console.log('GET API Error:', error.response?.status, error.response?.data);
          if (error.response && error.response.status !== 404) {
            console.error('Error fetching resignation form:', error);
            toast.error('Error loading existing form data');
          }
        });
    }
  }, [effectiveUserId, getAuthHeaders]);

  // Load all users for dropdowns
  useEffect(() => {
    const loadUsers = async () => {
      try {
        // Use authenticated public users endpoint (non-admin allowed)
        const res = await axios.get(`${API_URL}/users/public`, { headers: getAuthHeaders() });
        setUserOptions(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        console.error('Failed to load users list', e);
        setUserOptions([]);
      }
    };
    loadUsers();
  }, [getAuthHeaders]);

  const handleReasonChange = (reason) => {
    formik.setFieldValue('reasonForResignation', reason);
  };

  const handleFeedbackChange = (field, value) => {
    // Store all feedback values as strings
    formik.setFieldValue(`feedback.${field}`, value);
    
    // Mark the field as touched
    formik.setFieldTouched(`feedback.${field}`, true);
    
    // Trigger validation
    setTimeout(() => {
      formik.validateForm();
    }, 100);
  };

  return (
    <Paper elevation={3} sx={{ p: 3, m: 2, backgroundColor: 'white' }}>
      <form onSubmit={formik.handleSubmit}>
        <Typography variant="h4" sx={{ mb: 3, textAlign: 'center', fontWeight: 'bold' }}>
          RESIGNATION FORM
        </Typography>
        <fieldset disabled={readOnly} style={{ border: 0, padding: 0, margin: 0 }}>
        {/* Basic Information Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
            Employee Information
          </Typography>
         
         
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Name"
                name="name"
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                fullWidth
                required
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Employee ID"
                name="employeeId"
                value={formik.values.employeeId}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                fullWidth
                error={formik.touched.employeeId && Boolean(formik.errors.employeeId)}
                helperText={formik.touched.employeeId && formik.errors.employeeId}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Department"
                name="department"
                value={formik.values.department}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                fullWidth
                required
                InputProps={{ readOnly: true }}
                error={formik.touched.department && Boolean(formik.errors.department)}
                helperText={formik.touched.department && formik.errors.department}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Designation"
                name="designation"
                value={formik.values.designation}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                fullWidth
                required
               
                error={formik.touched.designation && Boolean(formik.errors.designation)}
                helperText={formik.touched.designation && formik.errors.designation}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required error={formik.touched.workLocation && Boolean(formik.errors.workLocation)}>
                <InputLabel>Work Location</InputLabel>
                <Select
                  name="workLocation"
                  value={formik.values.workLocation}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  label="Work Location"
                >
                  <MenuItem value="Mumbai">Mumbai</MenuItem>
                  <MenuItem value="Delhi">Delhi</MenuItem>
                  <MenuItem value="Remote">Remote</MenuItem>
                </Select>
                {formik.touched.workLocation && formik.errors.workLocation && (
                  <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                    {formik.errors.workLocation}
                  </Typography>
                )}
              </FormControl>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Reason for Resignation Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
            Reason for Resignation
          </Typography>
          <RadioGroup
            name="reasonForResignation"
            value={formik.values.reasonForResignation}
            onChange={(e) => handleReasonChange(e.target.value)}
            onBlur={formik.handleBlur}
          >
            <Grid container spacing={2}>
              {[
                'Better opportunity',
                'Career change',
                'Relocation',
                'Work environment',
                'Compensation and benefits',
                'Lack of growth opportunities',
                'Relationship with manager/team',
                'Job role mismatch',
                'Company culture',
                'Personal reasons',
                'Health reasons',
                'Other'
              ].map((reason) => (
                <Grid item xs={12} sm={6} md={4} key={reason}>
                  <FormControlLabel
                    value={reason}
                    control={<Radio />}
                    label={reason}
                  />
                </Grid>
              ))}
            </Grid>
          </RadioGroup>
          {formik.touched.reasonForResignation && Boolean(formik.errors.reasonForResignation) && (
            <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
              {formik.errors.reasonForResignation}
            </Typography>
          )}
          {formik.values.reasonForResignation === 'Other' && (
            <TextField
              label="Please explain other reason"
              name="otherReason"
              value={formik.values.otherReason}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              fullWidth
              multiline
              rows={2}
              sx={{ mt: 2 }}
              required
              error={formik.touched.otherReason && Boolean(formik.errors.otherReason)}
              helperText={formik.touched.otherReason && formik.errors.otherReason}
            />
          )}
        </Box>

        {/* Thinking Duration Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
            Since how long have you been thinking about this decision?
          </Typography>
          <RadioGroup
            name="thinkingDuration"
            value={formik.values.thinkingDuration}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          >
            <FormControlLabel value="1-2 weeks" control={<Radio />} label="1-2 weeks" />
            <FormControlLabel value="3-4 weeks" control={<Radio />} label="3-4 weeks" />
            <FormControlLabel value="5-6 weeks" control={<Radio />} label="5-6 weeks" />
            <FormControlLabel value="More than 6 weeks" control={<Radio />} label="More than 6 weeks" />
          </RadioGroup>
          {formik.touched.thinkingDuration && Boolean(formik.errors.thinkingDuration) && (
            <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
              {formik.errors.thinkingDuration}
            </Typography>
          )}
        </Box>

        {/* Incidents Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
            Were there a series of incidents which lead you to take this decision?
          </Typography>
          <RadioGroup
            name="incidentsLedToDecision"
            value={formik.values.incidentsLedToDecision}
            onChange={(e) => formik.setFieldValue('incidentsLedToDecision', e.target.value )}
            onBlur={formik.handleBlur}
          >
            <FormControlLabel value="true" control={<Radio />} label="Yes" />
            <FormControlLabel value="false" control={<Radio />} label="No" />
          </RadioGroup>
          {formik.touched.incidentsLedToDecision && Boolean(formik.errors.incidentsLedToDecision) && (
            <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
              {formik.errors.incidentsLedToDecision}
            </Typography>
          )}
          {formik.values.incidentsLedToDecision === 'true' && (
            <TextField
              label="Explain the scenario which lead to you taking the decision for resigning"
              name="scenarioExplanation"
              value={formik.values.scenarioExplanation}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              fullWidth
              multiline
              rows={4}
              sx={{ mt: 2 }}
              required
              error={formik.touched.scenarioExplanation && Boolean(formik.errors.scenarioExplanation)}
              helperText={formik.touched.scenarioExplanation && formik.errors.scenarioExplanation}
            />
          )}
        </Box>

        {/* Discussion Preference Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
            With whom would you like to discuss this matter first?
          </Typography>
          <RadioGroup
            name="discussionPreference"
            value={formik.values.discussionPreference}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          >
            <FormControlLabel value="Reporting Manager" control={<Radio />} label="Reporting Manager" />
            <FormControlLabel value="Unit Head" control={<Radio />} label="Unit Head" />
            <FormControlLabel value="HR" control={<Radio />} label="HR" />
          </RadioGroup>
          {formik.touched.discussionPreference && Boolean(formik.errors.discussionPreference) && (
            <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
              {formik.errors.discussionPreference}
            </Typography>
          )}
        </Box>

        {/* Dates and Manager Information */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
            Resignation Details
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Date of Resignation"
                name="dateOfResignation"
                type="date"
                value={formik.values.dateOfResignation}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                InputLabelProps={{ shrink: true }}
                fullWidth
                required
                error={formik.touched.dateOfResignation && Boolean(formik.errors.dateOfResignation)}
                helperText={formik.touched.dateOfResignation && formik.errors.dateOfResignation}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Proposed Last Day of Working"
                name="proposedLastWorkingDay"
                type="date"
                value={formik.values.proposedLastWorkingDay}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                InputLabelProps={{ shrink: true }}
                fullWidth
                required
                error={formik.touched.proposedLastWorkingDay && Boolean(formik.errors.proposedLastWorkingDay)}
                helperText={formik.touched.proposedLastWorkingDay && formik.errors.proposedLastWorkingDay}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
            <FormControl fullWidth required error={formik.touched.reportingManager && Boolean(formik.errors.reportingManager)}>
                <InputLabel>Reporting Manager</InputLabel>
              <Select
                label="Reporting Manager"
                name="reportingManager"
                value={formik.values.reportingManager}
                onChange={(e) => {
                  const name = e.target.value;
                  formik.setFieldValue('reportingManager', name);
                  const u = (userOptions || []).find((x) => `${x.firstName || ''} ${x.lastName || ''}`.trim() === name);
                  formik.setFieldValue('reportingManagerEmail', u?.email || '');
                }}
                onBlur={formik.handleBlur}
              >
                  <MenuItem value="">Select</MenuItem>
                  {/* Options populated below */}
                  {Array.isArray(userOptions) && userOptions.map((u) => (
                    <MenuItem key={u._id} value={`${u.firstName || ''} ${u.lastName || ''}`.trim()}>
                      {`${u.firstName || ''} ${u.lastName || ''}`.trim()} ({u.email})
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.reportingManager && formik.errors.reportingManager && (
                  <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                    {formik.errors.reportingManager}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
            <FormControl fullWidth required error={formik.touched.unitHead && Boolean(formik.errors.unitHead)}>
                <InputLabel>Unit Head</InputLabel>
              <Select
                label="Unit Head"
                name="unitHead"
                value={formik.values.unitHead}
                onChange={(e) => {
                  const name = e.target.value;
                  formik.setFieldValue('unitHead', name);
                  const u = (userOptions || []).find((x) => `${x.firstName || ''} ${x.lastName || ''}`.trim() === name);
                  formik.setFieldValue('unitHeadEmail', u?.email || '');
                }}
                onBlur={formik.handleBlur}
              >
                  <MenuItem value="">Select</MenuItem>
                  {Array.isArray(userOptions) && userOptions.map((u) => (
                    <MenuItem key={u._id} value={`${u.firstName || ''} ${u.lastName || ''}`.trim()}>
                      {`${u.firstName || ''} ${u.lastName || ''}`.trim()} ({u.email})
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.unitHead && formik.errors.unitHead && (
                  <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                    {formik.errors.unitHead}
                  </Typography>
                )}
              </FormControl>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Feedback Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
            Feedback for Resignation Discussion
          </Typography>
          <Typography variant="body2" sx={{ mb: 3, fontStyle: 'italic' }}>
            Thank you for your earlier request. To help us support you in the best possible way, we kindly ask you to take a moment to answer a few short questions. Your responses will enable us to better understand your needs and handle the next steps with care and attention.
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Were you satisfied with the career development and training opportunities?
              </Typography>
              <RadioGroup
                name="feedback.careerDevelopment"
                value={formik.values.feedback.careerDevelopment}
                onChange={(e) => handleFeedbackChange('careerDevelopment', e.target.value)}
                row
              >
                <FormControlLabel value="true" control={<Radio />} label="Yes" />
                <FormControlLabel value="false" control={<Radio />} label="No" />
              </RadioGroup>
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Was your workload manageable?
              </Typography>
              <RadioGroup
                name="feedback.workloadManageable"
                value={formik.values.feedback.workloadManageable}
                onChange={(e) => handleFeedbackChange('workloadManageable', e.target.value)}
                row
              >
                <FormControlLabel value="true" control={<Radio />} label="Yes" />
                <FormControlLabel value="false" control={<Radio />} label="No" />
              </RadioGroup>
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Were your skills and talents effectively utilized?
              </Typography>
              <RadioGroup
                name="feedback.skillsUtilized"
                value={formik.values.feedback.skillsUtilized}
                onChange={(e) => handleFeedbackChange('skillsUtilized', e.target.value)}
                row
              >
                <FormControlLabel value="true" control={<Radio />} label="Yes" />
                <FormControlLabel value="false" control={<Radio />} label="No" />
              </RadioGroup>
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                On a scale of 1-10; 10 being the highest, we would like to know your estimate on the scale for the effectiveness of utilising your skills and talents so far in your current role:
              </Typography>
              
              {/* Number Rating */}
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((rating) => (
                  <Button
                    key={rating}
                    variant={formik.values.feedback.skillsUtilizedRating === rating ? "contained" : "outlined"}
                    onClick={() => handleFeedbackChange('skillsUtilizedRating', rating)}
                    sx={{
                      minWidth: 40,
                      height: 40,
                      borderRadius: '50%',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      backgroundColor: formik.values.feedback.skillsUtilizedRating === rating ? 'primary.main' : 'transparent',
                      color: formik.values.feedback.skillsUtilizedRating === rating ? 'white' : 'primary.main',
                      borderColor: 'primary.main',
                      '&:hover': {
                        backgroundColor: formik.values.feedback.skillsUtilizedRating === rating ? 'primary.dark' : 'primary.light',
                        color: 'white',
                      }
                    }}
                  >
                    {rating}
                  </Button>
                ))}
              </Box>
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Did you feel supported by your manager and team?
              </Typography>
              <RadioGroup
                name="feedback.supportFromManagerTeam"
                value={formik.values.feedback.supportFromManagerTeam}
                onChange={(e) => handleFeedbackChange('supportFromManagerTeam', e.target.value)}
                row
              >
                <FormControlLabel value="true" control={<Radio />} label="Yes" />
                <FormControlLabel value="false" control={<Radio />} label="No" />
              </RadioGroup>
              {formik.values.feedback.supportFromManagerTeam === 'false' && (
                <TextField
                  label="If no, please elaborate"
                  name="feedback.supportExplanation"
                  value={formik.values.feedback.supportExplanation}
                  onChange={(e) => handleFeedbackChange('supportExplanation', e.target.value)}
                  onBlur={formik.handleBlur}
                  fullWidth
                  multiline
                  rows={2}
                  sx={{ mt: 2 }}
                  required
                  error={formik.touched.feedback?.supportExplanation && Boolean(formik.errors.feedback?.supportExplanation)}
                  helperText={formik.touched.feedback?.supportExplanation && formik.errors.feedback?.supportExplanation}
                />
              )}
            </Grid>
          </Grid>
        </Box>
        </fieldset>
        {/* Final Comments Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
            Final Comments
          </Typography>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            Is there anything else you'd like to share that could help the company improve or understand your decision to leave? (Optional open text)
          </Typography>
          <TextField
            name="finalComments"
            value={formik.values.finalComments}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            fullWidth
            multiline
            rows={4}
          />
        </Box>

        {/* Acknowledgment Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
            Acknowledgment
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, fontStyle: 'italic' }}>
            I hereby confirm that my decision to resign is voluntary, and I have provided honest feedback above.
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                name="acknowledgment"
                checked={formik.values.acknowledgment}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
            }
            label="I acknowledge the above statement"
          />
          {formik.touched.acknowledgment && Boolean(formik.errors.acknowledgment) && (
            <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
              {formik.errors.acknowledgment}
            </Typography>
          )}
        </Box>

        <Divider sx={{ my: 3 }} />
        
        {!readOnly && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={loading}
              sx={{ minWidth: 150, py: 1.5 }}
            >
              {loading ? 'Submitting...' : (isUpdate ? 'Update Form' : 'Submit Form')}
            </Button>
          </Box>
        )}
      </form>
    </Paper>
  );
};

    export default ResignationStep;
